import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import type { Envelope, Notice, NoticeLevel } from '../../lib/envelope'
import { patchSheets } from '../../lib/sheetCache'
import { campaignKeys } from './queries'
import type { CampaignSections, CampaignSheet } from './types'

type Schema = components['schemas']

/**
 * Every campaign and world-building mutation answers with the same envelope, so every
 * one is wired the same way: call, patch the cache with `changed`, surface `notices`.
 * No refetch and no invalidation anywhere in this file — see lib/envelope.ts.
 */
export type CampaignEnvelope = Envelope<CampaignSections>

const NOTICE_LEVELS: NoticeLevel[] = ['info', 'warn', 'error']

function toNotice(raw: Schema['Notice']): Notice {
  const level = raw.level as NoticeLevel | undefined
  return {
    level: level !== undefined && NOTICE_LEVELS.includes(level) ? level : 'info',
    code: raw.code ?? 'unknown',
    message: raw.message ?? '',
  }
}

/** Narrows the wire envelope. `changed` is untyped for the same reason `sections` is. */
export function toEnvelope(dto: Schema['CampaignEnvelope']): CampaignEnvelope {
  return {
    version: dto.version ?? 0,
    changed: (dto.changed ?? {}) as Partial<CampaignSections>,
    notices: (dto.notices ?? []).map(toNotice),
  }
}

/**
 * Patches both of a campaign's cache entries, each with the sections it holds.
 *
 * The campaign sheet and the world sheet are fed by two different reads, and one
 * mutation can touch both: adding a faction returns `factions` *and* `city`, because
 * the city's faction count moved. The world entry wants both; the sheet entry has no
 * `factions` key and must still get the new count. `patchSheets` does that filtering
 * — see lib/sheetCache.ts for why it is not simply written into both.
 */
export function patchCampaign(
  queryClient: QueryClient,
  campaignId: number,
  envelope: CampaignEnvelope,
): void {
  patchSheets<CampaignSections, CampaignSheet>(
    queryClient,
    [campaignKeys.sheet(campaignId), campaignKeys.world(campaignId)],
    envelope,
  )
}

export interface CampaignMutationOptions<TVariables> {
  campaignId: number
  request: (variables: TVariables) => Promise<Schema['CampaignEnvelope']>
  /** Receives every successful envelope, so one place can render all notices. */
  onEnvelope?: (envelope: CampaignEnvelope) => void
  /** Receives every failure, so one place can surface 409s and the rest. */
  onFailure?: (error: unknown) => void
}

/**
 * The campaign counterpart of `useSectionMutation`.
 *
 * There is no optimistic path here, and that is deliberate rather than unfinished.
 * The character sheet has two single-bit changes with an obvious inverse — a stress
 * box and a fate point — and optimism buys table pace on both. A campaign has none:
 * every write here changes a list *and* a server-computed count beside it
 * (`participantCount`, `characterCount`, `factionCount`, `canAddCharacter`), and
 * guessing at those would mean rendering numbers that might be wrong. Pending states
 * instead.
 */
export function useCampaignMutation<TVariables>(options: CampaignMutationOptions<TVariables>) {
  const queryClient = useQueryClient()

  return useMutation<CampaignEnvelope, unknown, TVariables>({
    mutationFn: async (variables) => toEnvelope(await options.request(variables)),

    onError: (error) => {
      options.onFailure?.(error)
    },

    onSuccess: (envelope) => {
      patchCampaign(queryClient, options.campaignId, envelope)
      options.onEnvelope?.(envelope)
    },
  })
}

// ---------------------------------------------------------------------------
// Request builders — one per endpoint, each returning the raw envelope DTO.
//
// Ids nested under a campaign (faction, member, relationship, location, note) are
// resolved from the loaded, access-checked graph server-side, so one belonging to
// another campaign simply 404s. That is why the routes take the campaign id even
// where the child id would be unique on its own.
// ---------------------------------------------------------------------------

type Envelopes = Schema['CampaignEnvelope']

export const campaignApi = {
  // ----- campaign ----------------------------------------------------------

  rename: (campaignId: number, name: string) =>
    api.put<Envelopes>(`/campaigns/${campaignId}`, { name }),

  // ----- membership --------------------------------------------------------

  addPlayer: (campaignId: number, userId: string) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/players`, { userId }),

  removePlayer: (campaignId: number, userId: string) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/players/${encodeURIComponent(userId)}`),

  // ----- roster ------------------------------------------------------------

  addCharacter: (campaignId: number, characterId: number) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/characters`, { characterId }),

  removeCharacter: (campaignId: number, characterId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/characters/${characterId}`),

  // ----- notes -------------------------------------------------------------

  addNote: (campaignId: number, body: Schema['NoteRequest']) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/notes`, body),

  updateNote: (campaignId: number, noteId: number, body: Schema['NoteRequest']) =>
    api.put<Envelopes>(`/campaigns/${campaignId}/notes/${noteId}`, body),

  removeNote: (campaignId: number, noteId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/notes/${noteId}`),

  // ----- city --------------------------------------------------------------

  createCity: (campaignId: number, body: Schema['CityRequest']) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/city`, body),

  updateCity: (campaignId: number, body: Schema['CityRequest']) =>
    api.put<Envelopes>(`/campaigns/${campaignId}/city`, body),

  removeCity: (campaignId: number) => api.delete<Envelopes>(`/campaigns/${campaignId}/city`),

  // ----- factions ----------------------------------------------------------

  addFaction: (campaignId: number, body: Schema['FactionRequest']) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/factions`, body),

  updateFaction: (campaignId: number, factionId: number, body: Schema['FactionRequest']) =>
    api.put<Envelopes>(`/campaigns/${campaignId}/factions/${factionId}`, body),

  removeFaction: (campaignId: number, factionId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/factions/${factionId}`),

  addRelationship: (
    campaignId: number,
    factionId: number,
    body: Schema['FactionRelationshipRequest'],
  ) => api.post<Envelopes>(`/campaigns/${campaignId}/factions/${factionId}/relationships`, body),

  updateRelationship: (
    campaignId: number,
    relationshipId: number,
    body: Schema['UpdateFactionRelationshipRequest'],
  ) =>
    api.put<Envelopes>(`/campaigns/${campaignId}/factions/relationships/${relationshipId}`, body),

  removeRelationship: (campaignId: number, relationshipId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/factions/relationships/${relationshipId}`),

  addMember: (campaignId: number, factionId: number, body: Schema['FactionMemberRequest']) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/factions/${factionId}/members`, body),

  updateMember: (
    campaignId: number,
    memberId: number,
    body: Schema['UpdateFactionMemberRequest'],
  ) => api.put<Envelopes>(`/campaigns/${campaignId}/factions/members/${memberId}`, body),

  removeMember: (campaignId: number, memberId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/factions/members/${memberId}`),

  // ----- locations ---------------------------------------------------------

  addLocation: (campaignId: number, body: Schema['LocationRequest']) =>
    api.post<Envelopes>(`/campaigns/${campaignId}/locations`, body),

  updateLocation: (campaignId: number, locationId: number, body: Schema['LocationRequest']) =>
    api.put<Envelopes>(`/campaigns/${campaignId}/locations/${locationId}`, body),

  removeLocation: (campaignId: number, locationId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/locations/${locationId}`),

  addLocationCharacter: (
    campaignId: number,
    locationId: number,
    body: Schema['LocationCharacterRequest'],
  ) => api.post<Envelopes>(`/campaigns/${campaignId}/locations/${locationId}/characters`, body),

  updateLocationCharacter: (
    campaignId: number,
    locationCharacterId: number,
    body: Schema['UpdateLocationCharacterRequest'],
  ) =>
    api.put<Envelopes>(
      `/campaigns/${campaignId}/locations/characters/${locationCharacterId}`,
      body,
    ),

  removeLocationCharacter: (campaignId: number, locationCharacterId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/locations/characters/${locationCharacterId}`),

  addLocationEncounter: (
    campaignId: number,
    locationId: number,
    body: Schema['LocationEncounterRequest'],
  ) => api.post<Envelopes>(`/campaigns/${campaignId}/locations/${locationId}/encounters`, body),

  updateLocationEncounter: (
    campaignId: number,
    encounterId: number,
    body: Schema['LocationEncounterRequest'],
  ) => api.put<Envelopes>(`/campaigns/${campaignId}/locations/encounters/${encounterId}`, body),

  removeLocationEncounter: (campaignId: number, encounterId: number) =>
    api.delete<Envelopes>(`/campaigns/${campaignId}/locations/encounters/${encounterId}`),
}
